import { toaster } from '@/shared/ui/toaster.component';
import { Button } from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  isProposalSentToInspectionError,
  useProposalAnalysisDecision,
} from '../../hooks';
import { useProposalDecisionForm } from '../../hooks/use-proposal-decision-form.hook';
import type { ProposalDecisionValues } from '../../types';
import { DecisionConfirmDialog } from './decision-confirm-dialog.component';
import { DecisionFormBody } from './decision-form-body.component';

interface ProposalDecisionFormProps {
  proposalId: string;
  requiresInspection?: string;
  onCancel: () => void;
}

export function ProposalDecisionForm({
  proposalId,
  requiresInspection,
  onCancel,
}: ProposalDecisionFormProps) {
  const navigate = useNavigate();
  const decideMutation = useProposalAnalysisDecision(proposalId);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const mainForm = useProposalDecisionForm({
    defaultValues: {
      status: null,
      acceptance: '',
      rejectionReason: '',
      rejectionObs: '',
      attachments: [],
    } as ProposalDecisionValues,
    onSubmit: async ({ value }: { value: ProposalDecisionValues }) => {
      try {
        const description =
          value.status === 'ACCEPT' ? value.acceptance : value.rejectionObs;

        await decideMutation.mutateAsync({
          decision: value.status === 'ACCEPT' ? 'accepted' : 'rejected',
          description,
          rejectionReason:
            value.status === 'REJECT' ? value.rejectionReason : undefined,
          attachments: value.attachments,
        });

        if (value.status === 'REJECT') {
          toaster.create({
            type: 'success',
            title: 'Proposta recusada',
            description: 'A decisão foi registrada com sucesso.',
          });
          mainForm.reset();
          void navigate({ to: '/proposal-analyses' });
          return;
        }

        toaster.create({
          type: 'success',
          title: 'Proposta aceita',
          description:
            'A proposta foi enviada para o processo de emissão da apólice.',
        });
        mainForm.reset();
        void navigate({ to: '/proposal-analyses' });
      } catch (error) {
        if (isProposalSentToInspectionError(error)) {
          toaster.create({
            type: 'success',
            title: 'Proposta aceita',
            description:
              'A proposta foi enviada para o processo de inspeção.',
          });
          mainForm.reset();
          void navigate({ to: '/proposal-analyses' });
          return;
        }
        toaster.create({
          type: 'error',
          title: 'Erro ao confirmar decisão',
          description:
            error instanceof Error ? error.message : 'Erro inesperado',
        });
      }
    },
  });

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex flex-col gap-4 bg-white shadow-xl rounded-2xl py-12 px-8'>
        <p className='font-montserrat text-base font-normal text-alba-black-500'>
          Decisão final
        </p>

        <DecisionFormBody form={mainForm} requiresInspection={requiresInspection} />
      </div>

      <div className='flex flex-row gap-4 justify-between items-center'>
        <Button variant='ghost' size='md' onClick={onCancel}>
          Cancelar
        </Button>

        <mainForm.Subscribe
          selector={(state: {
            values: { status: null | 'ACCEPT' | 'REJECT' };
            isSubmitting: boolean;
          }) => ({
            status: state.values.status,
            isSubmitting: state.isSubmitting,
          })}
          children={({
            status,
            isSubmitting,
          }: {
            status: null | 'ACCEPT' | 'REJECT';
            isSubmitting: boolean;
          }) => (
            <>
              <Button
                variant='primary'
                size='md'
                disabled={status === null || isSubmitting}
                onClick={() => setIsConfirmDialogOpen(true)}
              >
                Confirmar
              </Button>

              <DecisionConfirmDialog
                open={isConfirmDialogOpen}
                onOpenChange={setIsConfirmDialogOpen}
                status={status}
                requiresInspection={requiresInspection}
                isSubmitting={isSubmitting}
                onConfirm={async () => {
                  await mainForm.handleSubmit();
                  setIsConfirmDialogOpen(false);
                }}
              />
            </>
          )}
        />
      </div>
    </div>
  );
}
